import { message, Upload } from "antd";
import { type UploadFileStatus } from "antd/es/upload/interface";
import { type RcFile, type UploadChangeParam } from "antd/lib/upload";
import { type UploadFile } from "antd/lib/upload/interface";
import {
    BTFileSystem,
    BTServiceFileUpload,
    type IBTFileSystemData,
} from "legacyComponents/FileUploadContainer.types";
import { Component, type ComponentProps, type ReactNode } from "react";

import { BdsButton, BdsIconUploadSimple } from "@buildertrend/components";

import { type MediaType, type TempFileTypes } from "types/enum";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import {
    beforeFileUpload,
    fileTypeBlacklist,
    type IUploadConfig,
    onFileUploadDrop,
    showFailedUploadMessage,
    uploadAction,
} from "utilities/document/fileUpload.utils";
import { isNullOrUndefined } from "utilities/object/object";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTIconInboxOutlined, BTIconUploadOutlined } from "commonComponents/btWrappers/BTIcon";
import "commonComponents/btWrappers/BTUpload/BTUpload.less";
import { type ISelectedFile } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import { FileDragTarget } from "commonComponents/entity/media/FileDragTarget/FileDragTarget";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

interface IBTUploadProps {
    setFieldValue: (
        field: string,
        value: BTServiceFileUpload | BTServiceFileUpload[] | undefined,
    ) => void;
    setTempFile?: (tempFile: BTServiceFileUpload) => void;

    id: string;
    builderId?: number;
    "data-testid": string;
    readOnly?: boolean;

    disabled?: boolean;
    multiple?: boolean;
    isDragger?: boolean;
    isArea?: boolean;
    isRemovable?: boolean;

    /**
     * @example ["*"] // all files
     * @example ["jpg", "gif", "doc", "docx"]
     */
    allowedFileTypes: string[];
    /**
     * If a file matches the both the blacklist and whitelist props,
     * the file is NOT allowed. Blacklists take precedence over whitelists.
     * @example ["jpg", "gif", "doc", "docx"]
     * @default our standard blacklist
     */
    disallowedFileTypes: string[];

    tempFileType: TempFileTypes;
    mediaType?: MediaType;

    value: BTServiceFileUpload[] | BTServiceFileUpload | undefined;

    uploadConfig?: IUploadConfig;

    beforeUpload: (file: File, fileList: File[]) => Promise<boolean>;
    showUploadList: boolean;
    uploadButtonText?: string;
    onStatusChange?: (statuses: (UploadFileStatus | undefined)[]) => void;
    onError?: (error: Error, fileList: UploadFile[]) => void;

    /** Pass in formik setFieldTouched */
    onBlur?: (id: string, touched: boolean) => void;
    children?: ReactNode;
    bdsButtonTooltip?: string;
    useBdsButtonStyle?: boolean;
    hideDraggerDefaultContent?: boolean;
    icon?: ReactNode;
    buttonType?: ComponentProps<typeof BdsButton>["displayType"];
    loading?: boolean;
}

class BTUploadBase extends Component<IBTUploadProps & ITrackingProp> {
    static defaultProps = {
        disabled: false,
        disallowedFileTypes: fileTypeBlacklist,
        beforeUpload: async () => {
            return true;
        },
        uploadConfig: {
            shouldUploadVideosToStreamingService: false,
            shouldUploadFullResolutionPhoto: undefined,
        },
        showUploadList: true,
        onBlur: () => {},
        useBdsButtonStyle: false,
    };

    private handleBlur = () => {
        this.props.onBlur!(this.props.id, true);
    };

    private onChange = async (info: UploadChangeParam) => {
        switch (info.file.status) {
            case "error":
                showFailedUploadMessage(info.file.error);
                if (this.props.onError) {
                    this.props.onError(info.file.error, info.fileList);
                }
                return;
            case "done":
                const { setFieldValue, id, multiple } = this.props;

                let value: BTServiceFileUpload[] | BTServiceFileUpload | undefined = undefined;

                if (multiple) {
                    const incompleteFiles = info.fileList.filter(
                        (f) => f.status !== "done" && f.status !== undefined,
                    );

                    if (incompleteFiles.length === 0) {
                        const mappedFiles = info.fileList.map((f) => this.getAttachFileRequest(f));
                        const attachedFiles = await Promise.all(mappedFiles);
                        value = attachedFiles;
                    }
                } else {
                    const attachedFile = await this.getAttachFileRequest(info.file);
                    value = attachedFile;
                    if (this.props.setTempFile) {
                        this.props.setTempFile(value);
                    }
                }

                if (value !== undefined) {
                    setFieldValue(id, value);
                    this.handleBlur();
                }
                this.props.tracking?.trackEvent({ event: "ValueChange" });
        }
        if (this.props.onStatusChange) {
            this.props.onStatusChange(info.fileList.map((f) => f.status));
        }
    };

    private getAttachFileRequest(infoFile: UploadFile<any>) {
        const file = infoFile.originFileObj as Blob;

        // this is for files that are already in the list during subsequent file uploads
        // they already have the url, so we just need to map to the correct type
        if (infoFile.response === undefined) {
            return new Promise<BTServiceFileUpload>((resolve) => {
                const attachFileRequest = getBtUploadFile(infoFile);
                resolve(attachFileRequest);
            });
        } else {
            const fileDataObj = infoFile.response as IBTFileSystemData;
            const btFile = new BTFileSystem(fileDataObj);
            const attachFileRequest = mapToBTServiceFileUpload(btFile, true);
            const reader = new FileReader();

            return new Promise<BTServiceFileUpload>((resolve, reject) => {
                reader.addEventListener(
                    "load",
                    function () {
                        attachFileRequest.url = reader.result as string;
                        resolve(attachFileRequest);
                    },
                    false,
                );

                reader.addEventListener("error", function () {
                    void message.error(`An error occurred reading file: ${infoFile.name}.`);
                    reject(
                        reader.error ??
                            new Error(`FileReader error reading file: ${infoFile.name}`),
                    );
                });

                if (file !== undefined) {
                    reader.readAsDataURL(file);
                }
            });
        }
    }

    private onRemove = (file: UploadFile): boolean | void => {
        const { setFieldValue, id, multiple } = this.props;
        if (multiple) {
            const files = getFilesFromValue(this.props.value).filter(function (f) {
                return f.fileName !== file.name;
            });

            setFieldValue(id, files);
            this.handleBlur();
        } else {
            setFieldValue(id, undefined);
            this.handleBlur();
        }
    };

    render() {
        const {
            id,
            builderId,
            disabled,
            allowedFileTypes,
            disallowedFileTypes,
            value,
            readOnly,
            "data-testid": testid,
            tempFileType,
            uploadConfig,
            multiple,
            isDragger,
            isArea,
            mediaType,
            showUploadList,
            isRemovable,
            uploadButtonText,
            bdsButtonTooltip: tooltip,
            useBdsButtonStyle,
            hideDraggerDefaultContent,
            icon,
            loading,
        } = this.props;

        const allFileTypes = allowedFileTypes.length === 1 && allowedFileTypes[0] === "*";
        const allowedFileTypesWithDot = allowedFileTypes.map(
            (extensionWithoutDot) => "." + extensionWithoutDot,
        );
        const acceptedFileExtensions = allFileTypes ? undefined : allowedFileTypesWithDot.join();

        // convert selectedFile to uploadFile
        let existingFileList: UploadFile[] | undefined = undefined;
        const files = getFilesFromValue(value);
        existingFileList = files.map((f) => {
            return getUploadFile(f);
        });

        const uploadProps = {
            "data-testid": testid,
            key: JSON.stringify(existingFileList),
            customRequest: uploadAction(tempFileType, uploadConfig!, {
                mediaType,
                builderId,
            }),
            multiple: multiple,
            accept: acceptedFileExtensions,
            defaultFileList: existingFileList,
            onChange: this.onChange,
            onRemove: this.onRemove,
            beforeUpload: beforeFileUpload(
                allowedFileTypes,
                this.props.beforeUpload,
                false,
                disallowedFileTypes,
                mediaType,
            ),
            onDrop: onFileUploadDrop(allowedFileTypes, false),
            disabled: readOnly || disabled,
            showUploadList: !isNullOrUndefined(isRemovable)
                ? {
                      showRemoveIcon: isRemovable,
                  }
                : showUploadList,
        };

        if (readOnly) {
            return (
                <ValueDisplay
                    id={id}
                    value={
                        !existingFileList || existingFileList.length === 0 ? undefined : (
                            // eslint-disable-next-line react/forbid-elements
                            <Upload {...uploadProps} />
                        )
                    }
                />
            );
        }

        if (isDragger) {
            return (
                <div className="dropbox">
                    {/* eslint-disable-next-line react/forbid-elements */}
                    <Upload.Dragger {...uploadProps} className="BTUpload-draggerArea">
                        {!hideDraggerDefaultContent && (
                            <>
                                <p className="ant-upload-drag-icon">
                                    <BTIconInboxOutlined />
                                </p>
                                <p className="ant-upload-text">
                                    <BTExternalLink isUnderline={true}>Browse</BTExternalLink> or
                                    drag file{multiple ? "s" : ""} here to upload
                                </p>
                            </>
                        )}
                        {this.props.children}
                    </Upload.Dragger>
                </div>
            );
        }

        // When dropping file of incorrect type it will reject the file, but it may not give error toast.
        // onDrop only triggered when using Upload.Dragger component.
        if (isArea) {
            return (
                <FileDragTarget
                    allowDrop={!uploadProps.disabled}
                    forceDragOverDropEffect
                    render={() => (
                        // eslint-disable-next-line react/forbid-elements
                        <Upload {...uploadProps} className="BTUpload-UploadArea">
                            {this.props.children}
                        </Upload>
                    )}
                />
            );
        }

        let buttonActionText = value === undefined || multiple ? "Choose" : "Replace";
        let buttonFilesText: string | undefined = multiple ? "File(s)" : "File";

        if (uploadButtonText) {
            buttonActionText = uploadButtonText;
            buttonFilesText = undefined;
        }

        // key is set to defaultFileList as a hack to re-render the component whenever we update the list due to ant design copying props to state
        return (
            // eslint-disable-next-line react/forbid-elements
            <Upload {...uploadProps} className="BTUpload">
                {useBdsButtonStyle ? (
                    <BdsButton
                        displayType={this.props.buttonType ?? "primary"}
                        testid="upload"
                        disabled={disabled}
                        ariaLabel="Upload"
                        icon={icon ?? <BdsIconUploadSimple />}
                        text={uploadButtonText}
                        tooltip={tooltip}
                        loading={loading}
                    />
                ) : (
                    <BTButtonDeprecated
                        disabled={disabled}
                        data-testid="upload"
                        icon={<BTIconUploadOutlined />}
                    >
                        {buttonActionText} {buttonFilesText}
                    </BTButtonDeprecated>
                )}
            </Upload>
        );
    }
}

export const BTUpload = track((props) => ({ component: "Upload", uniqueId: props["data-testid"] }))(
    BTUploadBase,
);

function getUploadFile(value: BTServiceFileUpload) {
    const uploadFileMapped: UploadFile = {
        uid: value.tempId.toString(),
        size: 0,
        name: value.fileName,
        url: value.url,
        thumbUrl: value.imagePath,
        type: value.ext,
        linkProps: { download: value.fileName },
        originFileObj: {} as RcFile,
    };

    return uploadFileMapped;
}

function getBtUploadFile(value: UploadFile) {
    const uploadFileMapped: BTServiceFileUpload = {
        tempId: parseInt(value.uid),
        fileName: value.name,
        url: value.url ?? "",
        imagePath: value.thumbUrl ?? "",
        ext: value.type ?? "",
        hasChanged: true,
        fileSize: value.size ?? 0,
    };

    return uploadFileMapped;
}

function getFilesFromValue(value: BTServiceFileUpload[] | BTServiceFileUpload | undefined) {
    if (value !== undefined) {
        if (Array.isArray(value)) {
            return value;
        } else {
            return [value];
        }
    }
    return [];
}

/**
 * Maps a ISelectedFile or BTFile to a AttachFileRequest
 * @param hasChanged set to true when modified so the API updates the attachment
 */
export function mapToBTServiceFileUpload(
    file: ISelectedFile | BTFileSystem,
    hasChanged: boolean,
): BTServiceFileUpload {
    if (file instanceof BTFileSystem) {
        return new BTServiceFileUpload({
            ext: file.extension,
            fileName: file.title,
            hasChanged: true,
            imagePath: file.docPath,
            tempId: file.id ?? file.videoId,
            url: file.docPath,
            fileSize: file.fileSize,
        });
    }

    return new BTServiceFileUpload({
        ext: file.ext,
        fileName: file.title,
        hasChanged: hasChanged,
        imagePath: file.imagePath,
        tempId: file.id,
        url: file.url,
    });
}
